/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { UserCheck, UserX, Search, MapPin, Info, Edit3, X, Check, FileText, CheckCircle2, ChevronDown, ChevronUp, ShieldCheck, ShieldAlert, Users, Shield, Key, Eye, Clock, Calendar, Filter } from 'lucide-react';
import { StudentAvatar } from '../Common/StudentAvatar';
import { HeroBanner } from '../Common/HeroBanner';
import { formatLastNameFirst, sortStudentsByLastName } from '../../utils/studentUtils';
import { useInAppDialog } from '../Common/InAppDialog';

export const AdviseesPage: React.FC = () => {
  const { users, activeUser, updateUserProfile, schools, allSchools } = useWBL();
  const { showAlert, showConfirm, showToast } = useInAppDialog();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'verified' | 'pending' | 'delayed' | 'denied'>('all');
  const [classYearFilter, setClassYearFilter] = useState<string>('all');
  const [editingNotesUid, setEditingNotesUid] = useState<string | null>(null);
  const [notesInput, setNotesInput] = useState('');
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const resolveSchoolName = (schoolRef: string, schoolId?: string) => {
    if (schoolId) {
      const found = (allSchools || []).find(s => s.id === schoolId);
      if (found) return found.name;
    }
    if (!schoolRef) return 'Unspecified School';
    const found = (allSchools || []).find(s => s.id === schoolRef || s.name.toLowerCase() === schoolRef.toLowerCase());
    return found ? found.name : schoolRef;
  };

  const advisorSchools = useMemo(() => {
    if (!activeUser) return [];
    const referenceIds = Array.from(new Set([
      activeUser.schoolId,
      activeUser.signupSchoolId,
      ...(activeUser.schoolsServedIds || [])
    ].filter(Boolean) as string[]));

    if (referenceIds.length > 0) {
      return (allSchools || []).filter(s => referenceIds.includes(s.id));
    }
    return [];
  }, [allSchools, schools, activeUser]);

  const advisorSchoolIds = useMemo(() => advisorSchools.map(s => s.id), [advisorSchools]);

  const isUnattachedAdvisor = useMemo(() => {
    return activeUser?.role === 'advisor' && advisorSchoolIds.length === 0;
  }, [activeUser, advisorSchoolIds]);

  const designateAdvisees = useMemo(() => {
    if (!activeUser || advisorSchoolIds.length === 0) return [];
    if (activeUser.role === 'advisor' && !activeUser.verified) {
      return [];
    }

    return (users || []).filter(u => {
      if (u.role !== 'student') return false;
      if (!u.schoolId && !u.school && (!u.additionalSchoolIds || u.additionalSchoolIds.length === 0)) return false;

      const ferpaGranted = typeof u.ferpaConsent === 'object'
        ? u.ferpaConsent?.granted === true
        : u.ferpaConsent === true;
      if (!ferpaGranted) return false;

      if (u.schoolId && advisorSchoolIds.includes(u.schoolId)) return true;

      if (u.school) {
        const studentSchoolNorm = u.school.toLowerCase().trim();
        const studentSchool = (allSchools || []).find(s => {
          const normSchoolId = s.id.toLowerCase().trim();
          const normSchoolName = s.name.toLowerCase().trim();
          return studentSchoolNorm === normSchoolId || studentSchoolNorm === normSchoolName;
        });

        if (studentSchool && advisorSchoolIds.includes(studentSchool.id)) return true;
      }

      if (u.additionalSchoolIds && u.additionalSchoolIds.some(id => advisorSchoolIds.includes(id))) return true;

      if (u.additionalSchools && u.additionalSchools.length > 0) {
        const matchesName = u.additionalSchools.some(schoolName => {
          const studentSchoolNorm = schoolName.toLowerCase().trim();
          const studentSchool = (allSchools || []).find(s => {
            const normSchoolId = s.id.toLowerCase().trim();
            const normSchoolName = s.name.toLowerCase().trim();
            return studentSchoolNorm === normSchoolId || studentSchoolNorm === normSchoolName;
          });
          return studentSchool && advisorSchoolIds.includes(studentSchool.id);
        });
        if (matchesName) return true;
      }

      return false;
    });
  }, [users, allSchools, advisorSchoolIds, activeUser]);

  // Available unique class years for table header filter
  const availableClassYears = useMemo(() => {
    const set = new Set<string>();
    designateAdvisees.forEach(s => {
      if (s.classYear) set.add(s.classYear);
    });
    return Array.from(set).sort();
  }, [designateAdvisees]);

  const filteredAdvisees = useMemo(() => {
    const list = designateAdvisees.filter(student => {
      const nameMatch = (student.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (`${student.contactFirstName || ''} ${student.contactLastName || ''}`).toLowerCase().includes(searchQuery.toLowerCase());
      const emailMatch = (student.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      const resolvedSchool = resolveSchoolName(student.school || '', student.schoolId);
      const schoolMatch = resolvedSchool.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSearch = nameMatch || emailMatch || schoolMatch;

      const isVerified = !!student.advisorVerified || student.verificationStatus === 'verified';
      const isDenied = !!student.advisorDenied || student.verificationStatus === 'denied';
      const isDelayed = !!student.advisorDelayed || student.verificationStatus === 'delayed';

      const matchesStatus = statusFilter === 'all' || 
                            (statusFilter === 'verified' && isVerified) || 
                            (statusFilter === 'denied' && isDenied) ||
                            (statusFilter === 'delayed' && isDelayed) ||
                            (statusFilter === 'pending' && !isVerified && !isDenied && !isDelayed);

      const matchesClassYear = classYearFilter === 'all' || (student.classYear || '').toLowerCase().trim() === classYearFilter.toLowerCase().trim();

      return matchesSearch && matchesStatus && matchesClassYear;
    });

    return sortStudentsByLastName(list, (s: any) => s.name, (s: any) => s);
  }, [designateAdvisees, searchQuery, statusFilter, classYearFilter, allSchools]);

  const totalCount = designateAdvisees.length;
  const verifiedCount = designateAdvisees.filter(s => s.advisorVerified || s.verificationStatus === 'verified').length;
  const deniedCount = designateAdvisees.filter(s => s.advisorDenied || s.verificationStatus === 'denied').length;
  const delayedCount = designateAdvisees.filter(s => s.advisorDelayed || s.verificationStatus === 'delayed').length;
  const pendingCount = totalCount - verifiedCount - deniedCount - delayedCount;

  const handleVerify = async (studentUid: string, notes: string) => {
    if (activeUser?.role === 'advisor' && !activeUser?.verified) {
      showAlert({
        title: "Advisor Verification Required",
        message: "Unauthorized Action: Your advisor profile must be verified by a regional administrator before you can sign off on and verify student profiles.",
        type: "warning",
      });
      return;
    }
    try {
      const today = new Date().toISOString().split('T')[0];
      await updateUserProfile(studentUid, {
        advisorVerified: true,
        advisorVerifiedBy: activeUser?.name || activeUser?.email || 'School Advisor',
        advisorVerificationDate: today,
        advisorVerificationNotes: notes || 'Enrolled status verified by school advisor.',
        verified: true,
        verifiedBy: activeUser?.name || activeUser?.email || 'School Advisor',
        verifiedAt: new Date().toISOString(),
        verificationNotes: notes || 'Enrolled status verified by school advisor.',
        advisorDenied: false,
        advisorDelayed: false,
        verificationStatus: 'verified'
      });
      showToast({
        title: "Student Verified",
        message: "Successfully verified student profile.",
        type: "success",
      });
      setEditingNotesUid(null);
      setNotesInput('');
    } catch (e: any) {
      console.error("Failed to verify student:", e);
      showAlert({
        title: "Verification Failed",
        message: "Failed to verify student: " + (e?.message || "Please retry"),
        type: "error",
      });
    }
  };

  const handleDelay = async (studentUid: string, notes: string) => {
    if (activeUser?.role === 'advisor' && !activeUser?.verified) {
      showAlert({
        title: "Advisor Verification Required",
        message: "Unauthorized Action: Your advisor profile must be verified by a regional administrator before you can modify student verification status.",
        type: "warning",
      });
      return;
    }
    try {
      const today = new Date().toISOString().split('T')[0];
      await updateUserProfile(studentUid, {
        advisorVerified: false,
        verified: false,
        advisorDenied: false,
        advisorDelayed: true,
        verificationStatus: 'delayed',
        advisorDelayedBy: activeUser?.name || activeUser?.email || 'School Advisor',
        advisorDelayedDate: today,
        advisorDelayedNotes: notes || 'Verification delayed until appropriate graduation year / eligibility (e.g. 10th grader - eligible starting 11th grade).'
      });
      showToast({
        title: "Verification Delayed",
        message: "Student verification delayed until appropriate graduation year.",
        type: "info",
      });
      setEditingNotesUid(null);
      setNotesInput('');
    } catch (e: any) {
      console.error("Failed to delay verification:", e);
      showAlert({
        title: "Action Failed",
        message: "Failed to delay verification: " + (e?.message || "Please retry"),
        type: "error",
      });
    }
  };

  const handleRevoke = async (studentUid: string) => {
    if (activeUser?.role === 'advisor' && !activeUser?.verified) {
      showAlert({
        title: "Advisor Verification Required",
        message: "Unauthorized Action: Your advisor profile must be verified by a regional administrator before you can modify student verification status.",
        type: "warning",
      });
      return;
    }
    const confirmed = await showConfirm({
      title: "Reset Verification Status",
      message: "Are you sure you want to reset verification for this student? This will set their profile back to Pending status.",
      confirmText: "Reset to Pending",
      type: "warning",
    });
    if (!confirmed) return;

    try {
      await updateUserProfile(studentUid, {
        advisorVerified: false,
        advisorVerifiedBy: undefined,
        advisorVerificationDate: undefined,
        advisorVerificationNotes: undefined,
        verified: false,
        verifiedBy: undefined,
        verifiedAt: undefined,
        verificationNotes: undefined,
        advisorDenied: false,
        advisorDelayed: false,
        verificationStatus: 'pending'
      });
      showToast({
        title: "Status Reset",
        message: "Student status set back to Pending.",
        type: "info",
      });
    } catch (e: any) {
      console.error("Failed to reset verification:", e);
      showAlert({
        title: "Reset Failed",
        message: "Failed to reset verification status: " + (e?.message || "Please retry"),
        type: "error",
      });
    }
  };

  const handleDeny = async (studentUid: string) => {
    if (activeUser?.role === 'advisor' && !activeUser?.verified) {
      showAlert({
        title: "Advisor Verification Required",
        message: "Unauthorized Action: Your advisor profile must be verified by a regional administrator before you can modify student verification status.",
        type: "warning",
      });
      return;
    }
    const confirmed = await showConfirm({
      title: "Deny Student Verification",
      message: "Are you sure you want to mark this student account as DENIED / INVALID? This flags the profile and prevents them from applying for placement opportunities.",
      confirmText: "Deny Verification",
      type: "danger",
    });
    if (!confirmed) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      await updateUserProfile(studentUid, {
        advisorVerified: false,
        verified: false,
        advisorDenied: true,
        advisorDelayed: false,
        verificationStatus: 'denied',
        advisorDeniedBy: activeUser?.name || activeUser?.email || 'School Advisor',
        advisorDeniedDate: today,
        advisorDeniedNotes: 'Account verification denied (invalid or unverified school account).'
      });
      showToast({
        title: "Student Verification Denied",
        message: "Student account marked as denied/invalid.",
        type: "info",
      });
    } catch (e: any) {
      console.error("Failed to deny verification:", e);
      showAlert({
        title: "Denial Failed",
        message: "Failed to deny student verification: " + (e?.message || "Please retry"),
        type: "error",
      });
    }
  };

  const handleSaveNotes = async (studentUid: string, isVerified: boolean, isDelayed: boolean) => {
    try {
      if (isDelayed) {
        await updateUserProfile(studentUid, {
          advisorDelayedNotes: notesInput
        });
      } else if (isVerified) {
        await updateUserProfile(studentUid, {
          advisorVerificationNotes: notesInput
        });
      } else {
        await updateUserProfile(studentUid, {
          advisorVerificationNotes: notesInput
        });
      }
      setSaveSuccess(`Notes updated successfully.`);
      setTimeout(() => setSaveSuccess(null), 3000);
      setEditingNotesUid(null);
      setNotesInput('');
    } catch (e) {
      console.error("Failed to save notes:", e);
    }
  };

  return (
    <div className="space-y-6 font-sans animate-fade-in">
      <HeroBanner 
        title="ADVISEES DIRECTORY"
        subtitle={`MANAGING ${totalCount} ASSIGNED STUDENTS ACROSS HIGH SCHOOL PATHWAYS`}
        pageKey="advisees"
      />

      <div className="w-[95%] mx-auto space-y-6 pb-12">
        {saveSuccess && (
          <div className="bg-slate-900 text-white rounded-xl py-3 px-4 flex items-center space-x-2.5 text-xs font-bold shadow-lg animate-fade-in">
            <Check className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{saveSuccess}</span>
          </div>
        )}

        {/* Advisor Verification Warning */}
        {activeUser?.role === 'advisor' && !activeUser?.verified && (
          <div className="bg-amber-50/75 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-3xs text-left animate-fade-in">
            <div className="flex items-start space-x-3.5">
              <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                <ShieldAlert className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <h4 className="text-xs font-black text-amber-900 uppercase tracking-wider">Advisor Verification Pending</h4>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                  Your advisor account is awaiting administrator credential audit. Student records will be fully accessible once your profile is verified.
                </p>
              </div>
            </div>
            <span className="text-[9px] font-black uppercase bg-amber-100 text-amber-800 px-2.5 py-1 rounded-lg border border-amber-200 shrink-0">
              Read-Only Access
            </span>
          </div>
        )}

        {/* Cohesive Container matching Superadmin Users Page */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden flex flex-col">
          <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-5 h-5 text-[#0094c8]" />
              <div>
                <h2 className="text-lg font-black text-[#011d3c] tracking-tight uppercase">
                  Assigned Advisees Ledger
                </h2>
                <p className="text-[10px] text-slate-400 uppercase font-extrabold tracking-wider mt-0.5">
                  Review student verification status, graduation eligibility, and FERPA consent.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[280px]">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search advisees (name, email, school)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/50 border border-slate-200 outline-none rounded-xl py-2 pl-9 pr-4 focus:ring-1 focus:ring-custom-primary font-medium text-xs"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 tracking-wider">
                  <th className="py-4 px-6">Student Identity</th>
                  <th className="py-4 px-6">
                    <div className="flex items-center justify-between gap-2">
                      <span>School & Pathway</span>
                      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-2xs">
                        <Filter className="w-3 h-3 text-[#0094c8]" />
                        <select
                          value={classYearFilter}
                          onChange={(e) => setClassYearFilter(e.target.value)}
                          className="bg-transparent text-[10px] font-bold text-slate-700 outline-none cursor-pointer uppercase pr-1"
                          title="Filter by Graduation Year"
                        >
                          <option value="all">All Years</option>
                          {availableClassYears.map(y => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </th>
                  <th className="py-4 px-6">FERPA Consent</th>
                  <th className="py-4 px-6">
                    <div className="flex items-center justify-between gap-2">
                      <span>Verification Status</span>
                      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-2xs">
                        <Filter className="w-3 h-3 text-[#0094c8]" />
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value as any)}
                          className="bg-transparent text-[10px] font-bold text-slate-700 outline-none cursor-pointer uppercase pr-1"
                          title="Filter by Verification Status"
                        >
                          <option value="all">All Status ({totalCount})</option>
                          <option value="pending">Pending ({pendingCount})</option>
                          <option value="verified">Verified ({verifiedCount})</option>
                          <option value="delayed">Delayed ({delayedCount})</option>
                          <option value="denied">Denied ({deniedCount})</option>
                        </select>
                      </div>
                    </div>
                  </th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {filteredAdvisees.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-slate-400 font-medium">
                      <Info className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      {activeUser?.role === 'advisor' && !activeUser?.verified ? (
                        <div className="max-w-md mx-auto px-6">
                          <p className="text-sm font-bold text-slate-800">Verification Required</p>
                          <p className="text-[12px] text-slate-500 mt-2 leading-relaxed">
                            Your advisor account is currently awaiting administrator credential verification. Student records will be accessible once your account has been verified.
                          </p>
                        </div>
                      ) : isUnattachedAdvisor ? (
                        <div className="max-w-md mx-auto px-6">
                          <p className="text-sm font-bold text-slate-700">No Advisees Found</p>
                          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                            Your roster is currently empty. Once students are registered under your high school campus with signed FERPA consent, they will appear here.
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm">No advisees found matching the selected filters.</p>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredAdvisees.map((student) => {
                    const isVerified = !!student.advisorVerified || student.verificationStatus === 'verified';
                    const isDenied = !!student.advisorDenied || student.verificationStatus === 'denied';
                    const isDelayed = !!student.advisorDelayed || student.verificationStatus === 'delayed';
                    const isEditingNotes = editingNotesUid === student.uid;
                    const ferpaGranted = typeof student.ferpaConsent === 'object' ? student.ferpaConsent?.granted === true : student.ferpaConsent === true;
                    const ferpaDate = student.ferpaConsentDate || (typeof student.ferpaConsent === 'object' ? student.ferpaConsent?.date : '');

                    return (
                      <React.Fragment key={student.uid}>
                        <tr className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-6">
                            <div className="flex items-center space-x-3">
                              <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 border border-slate-100 bg-slate-50">
                                <StudentAvatar studentId={student.uid} studentName={formatLastNameFirst(student.name, student)} profile={student} />
                              </div>
                              <div>
                                <span className="font-extrabold text-slate-800 text-sm block">
                                  {formatLastNameFirst(student.name, student)}
                                </span>
                                <p className="text-[10px] text-slate-400 mt-0.5">{student.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="space-y-0.5">
                              <span className="font-bold text-slate-800 block">
                                {resolveSchoolName(student.school || '', student.schoolId)}
                              </span>
                              <span className="text-[10px] text-slate-400 font-mono block">
                                {student.academicLevel || 'Clinical'} • <span className="font-bold text-[#0094c8]">{student.classYear || 'Class of 2026'}</span>
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            {ferpaGranted ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-300 text-[9px] font-extrabold uppercase tracking-wider">
                                <Check className="w-3 h-3 text-emerald-600" />
                                <span>Signed {ferpaDate ? `(${new Date(ferpaDate).toLocaleDateString()})` : ''}</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-300 text-[9px] font-extrabold uppercase tracking-wider">
                                <X className="w-3 h-3 text-amber-600" />
                                <span>Pending</span>
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-6">
                            <span className={`px-2.5 py-1 rounded-lg text-[9px] uppercase font-black border inline-flex items-center space-x-1.5 ${
                              isVerified 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                : isDelayed
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : isDenied
                                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                                    : 'bg-amber-50 text-amber-600 border-amber-200'
                            }`}>
                              {isVerified ? (
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                              ) : isDelayed ? (
                                <Clock className="w-3.5 h-3.5 text-blue-600" />
                              ) : isDenied ? (
                                <UserX className="w-3.5 h-3.5 text-rose-600" />
                              ) : (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                              )}
                              <span>{isVerified ? 'Verified' : isDelayed ? 'Delayed' : isDenied ? 'Denied' : 'Pending'}</span>
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingNotesUid(isEditingNotes ? null : student.uid);
                                  setNotesInput(student.advisorVerificationNotes || student.advisorDelayedNotes || student.advisorDeniedNotes || '');
                                }}
                                className="inline-flex items-center space-x-1 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-all cursor-pointer"
                                title="Edit verification or delay notes"
                              >
                                <Edit3 className="w-3 h-3 text-slate-500" />
                                <span>Notes</span>
                              </button>

                              {isVerified ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleRevoke(student.uid)}
                                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                  >
                                    Reset
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelay(student.uid, 'Verification delayed until appropriate graduation year.')}
                                    className="px-2.5 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                    title="Delay verification for younger students"
                                  >
                                    Delay
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeny(student.uid)}
                                    className="px-2.5 py-1.5 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                  >
                                    Deny
                                  </button>
                                </>
                              ) : isDelayed ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={activeUser?.role === 'advisor' && !activeUser?.verified}
                                    onClick={() => handleVerify(student.uid, 'Enrolled status verified by school advisor.')}
                                    className="px-3 py-1.5 rounded-lg font-black text-[10px] uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white transition-all shadow-xs cursor-pointer"
                                  >
                                    Verify
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRevoke(student.uid)}
                                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                  >
                                    Reset
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeny(student.uid)}
                                    className="px-2.5 py-1.5 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                  >
                                    Deny
                                  </button>
                                </>
                              ) : isDenied ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={activeUser?.role === 'advisor' && !activeUser?.verified}
                                    onClick={() => handleVerify(student.uid, 'Enrolled status verified by school advisor.')}
                                    className="px-3 py-1.5 rounded-lg font-black text-[10px] uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white transition-all shadow-xs cursor-pointer"
                                  >
                                    Verify
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRevoke(student.uid)}
                                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                  >
                                    Reset
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    disabled={activeUser?.role === 'advisor' && !activeUser?.verified}
                                    onClick={() => handleVerify(student.uid, 'Enrolled status verified by school advisor.')}
                                    className="px-3 py-1.5 rounded-lg font-black text-[10px] uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white transition-all shadow-xs cursor-pointer"
                                  >
                                    Verify
                                  </button>
                                  <button
                                    type="button"
                                    disabled={activeUser?.role === 'advisor' && !activeUser?.verified}
                                    onClick={() => handleDelay(student.uid, 'Verification delayed until appropriate graduation year.')}
                                    className="px-2.5 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                    title="Delay verification for younger students (e.g. 10th grader)"
                                  >
                                    Delay
                                  </button>
                                  <button
                                    type="button"
                                    disabled={activeUser?.role === 'advisor' && !activeUser?.verified}
                                    onClick={() => handleDeny(student.uid)}
                                    className="px-2.5 py-1.5 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-black text-[10px] uppercase tracking-wider transition-all cursor-pointer"
                                  >
                                    Deny
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>

                        {isEditingNotes && (
                          <tr className="bg-slate-50/80">
                            <td colSpan={5} className="p-4">
                              <div className="space-y-2 max-w-2xl ml-auto">
                                <label className="block text-[10px] font-black uppercase text-slate-500 tracking-wider">
                                  Verification / Delay Remarks for {formatLastNameFirst(student.name, student)}
                                </label>
                                <textarea
                                  placeholder="Type student verification remarks or graduation delay notes here..."
                                  value={notesInput}
                                  onChange={(e) => setNotesInput(e.target.value)}
                                  className="w-full p-3 bg-white text-xs font-semibold border border-slate-200 focus:ring-1 focus:ring-[#0094c8] outline-none rounded-xl"
                                  rows={2}
                                />
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => { setEditingNotesUid(null); setNotesInput(''); }}
                                    className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-black px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider cursor-pointer"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveNotes(student.uid, isVerified, isDelayed)}
                                    className="bg-[#0094c8] hover:bg-[#007ba6] text-white font-black px-3.5 py-1.5 rounded-lg text-[10px] uppercase tracking-wider cursor-pointer"
                                  >
                                    Save Notes
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
